import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useApi } from '@/hooks/useApi';

/**
 * Icon-button that opens a Dialog showing the invite's proof screenshot.
 * Shared between the outreacher-facing Invites list (`scope="portal"`) and
 * the admin Payroll overview panel (`scope="admin"`). Admin scope hits a
 * dedicated route so admins don't have to impersonate just to view an
 * image belonging to another inviter.
 */
export function ScreenshotViewer({
  publicId,
  targetIdentifier,
  hasScreenshot,
  scope,
  size = 'icon',
}: {
  publicId: string;
  targetIdentifier: string;
  hasScreenshot: boolean;
  scope: 'portal' | 'admin';
  /** 'icon' = 36px button (Invites row), 'sm' = compact for dense rows. */
  size?: 'icon' | 'sm';
}): JSX.Element {
  const api = useApi();
  const [open, setOpen] = useState(false);

  const screenshotQuery = useQuery({
    enabled: open && hasScreenshot,
    queryKey: ['invite-screenshot', scope, publicId],
    queryFn: () =>
      api
        .get<{ url: string }>(
          scope === 'admin'
            ? `/admin/invites/${publicId}/screenshot`
            : `/portal/invites/${publicId}/screenshot`,
        )
        .then((v) => v.url),
    staleTime: 5 * 60_000,
  });

  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <>
      <Button
        size={size === 'sm' ? 'sm' : 'icon'}
        variant="ghost"
        disabled={!hasScreenshot}
        title={hasScreenshot ? 'View screenshot' : 'No screenshot uploaded'}
        onClick={() => setOpen(true)}
        className={size === 'sm' ? 'h-6 w-6 p-0' : undefined}
      >
        <ImageIcon className={iconClass} />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>@{targetIdentifier}</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-[240px] items-center justify-center">
            {screenshotQuery.isLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : screenshotQuery.error ? (
              <p className="text-sm text-destructive">
                {(screenshotQuery.error as Error)?.message ?? 'Failed to load screenshot'}
              </p>
            ) : screenshotQuery.data ? (
              <img
                src={screenshotQuery.data}
                alt={`Screenshot for @${targetIdentifier}`}
                className="max-h-[80vh] w-full rounded-md border object-contain"
              />
            ) : (
              <p className="text-sm text-muted-foreground">No screenshot available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
